import { compact, filter, find, includes, isEmpty } from 'lodash-es';
import { getChannelIcon } from '@/pages/aigc/pages/Agent/components/QAInput/KnowledgeBaseMention/utils/channelIconMap';
import { getSkillDisplayName } from '@/pages/aigc/pages/Agent/SkillsCollection/utils/display';
import SkillsDefaultIcon from '@/pages/aigc/assets/svg/skills-default-icon.svg';
import type { ISkillData } from '@/pages/aigc/pages/Agent/SkillsCollection/types';
import { FILE_TYPE_OPTIONS } from '../constants';
import type {
  TDatabrainFile,
  TFileLibrarySourceItem,
} from '../types';

/**
 * Agent 来源列展示名
 * @param agent - 授权 Agent，未命中时用 modelId
 * @param modelId - 文件 model_id
 * @param isEN - 是否英文界面
 */
export const getAgentSourceDisplayName = (
  agent: { name: string; name_cn?: string; } | undefined,
  modelId: string,
  isEN: boolean,
): string => {
  if (!agent) {
    return modelId;
  }
  if (isEN) {
    return agent.name;
  }
  return agent.name_cn || agent.name;
};

/**
 * 字节大小转展示文案
 * @param bytes - 文件字节数
 * @returns 如 "999.6 MB"
 * @example formatFileSize(1048576) // "1.0 MB"
 */
export const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const digits = unitIndex === 0 ? 0 : 1;
  return `${size.toFixed(digits)} ${units[unitIndex]}`;
};

/** 接口 file_type 归一化为筛选项 value */
const FILE_TYPE_ALIAS: Record<string, string> = {
  pptx: 'ppt',
  doc: 'word',
  docx: 'word',
  xls: 'excel',
  xlsx: 'excel',
  markdown: 'md',
  htm: 'html',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  bmp: 'image',
  svg: 'image',
};

/** 筛选项 value → 接口 conditions.file_type */
const FILTER_TO_API_FILE_TYPES: Record<string, string[]> = {
  word: ['doc', 'docx'],
  ppt: ['ppt', 'pptx'],
  excel: ['xls', 'xlsx'],
  html: ['html', 'htm'],
  md: ['md', 'markdown'],
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'],
};

/**
 * 筛选项 value 转接口 file_type 列表
 * @param fileType - 筛选文件类型
 */
export const resolveApiFileTypes = (fileType: string): string[] =>
  FILTER_TO_API_FILE_TYPES[fileType] ?? [fileType];

/**
 * 归一化 file_type 为 FILE_TYPE_OPTIONS 的 value
 * @param fileType - 接口 file_type
 */
const resolveFileTypeValue = (fileType: string): string => {
  const type = (fileType || '').toLowerCase();
  if (find(FILE_TYPE_OPTIONS, item => item.value === type)) {
    return type;
  }
  return FILE_TYPE_ALIAS[type] || type;
};

/**
 * 类型标签修饰符：用于 BEM `--pdf` 等
 * @param fileType - 接口 file_type
 */
export const getFileTypeModifier = (fileType: string): string =>
  find(FILE_TYPE_OPTIONS, item => item.value === resolveFileTypeValue(fileType))
    ?.modifier || 'default';

/**
 * 类型标签展示文案
 * @param fileType - 接口 file_type
 */
export const getFileTypeLabel = (fileType: string): string =>
  find(FILE_TYPE_OPTIONS, item => item.value === resolveFileTypeValue(fileType))
    ?.tagLabel || (fileType || '').toUpperCase();

/**
 * 文件库行图标：与知识库 @ 下拉同一套 channelIconMap
 * @param fileType - 接口 file_type
 */
export const getFileLibraryIcon = (fileType: string): string => {
  const value = resolveFileTypeValue(fileType);
  if (value === 'image') {
    return getChannelIcon('image');
  }
  const channelFileType: Record<string, string> = {
    word: 'docx',
    excel: 'xlsx',
    ppt: 'pptx',
    md: 'md',
  };
  return getChannelIcon(undefined, channelFileType[value] || value);
};

/**
 * 来源下拉按名称本地过滤
 * @param items - 当前 Tab 全量列表
 * @param keyword - 搜索词
 */
export const filterSourceItems = (
  items: TFileLibrarySourceItem[],
  keyword: string,
): TFileLibrarySourceItem[] => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return items;
  return filter(items, item => includes(item.name.toLowerCase(), kw));
};

/** 来源列展示类型 */
export type TFileSourceDisplayKind = 'skill' | 'agent';

/**
 * 按 skills_used 包名匹配 Skill 列表项
 * @param packageName - 接口 skills_used 元素
 * @param allSkills - Skill 全量列表
 */
export const findSkillByPackageName = (
  packageName: string,
  allSkills: ISkillData[],
): ISkillData | undefined =>
  find(
    allSkills,
    item => item.skill_name === packageName || item.skill_id === packageName,
  );

/**
 * skills_used 包名 → UI 展示名；未命中返回 undefined，不回退包名
 * @param packageName - 接口 skills_used 元素
 * @param allSkills - Skill 全量列表
 * @param isEN - 是否英文界面
 */
export const getSkillUsedDisplayLabel = (
  packageName: string,
  allSkills: ISkillData[],
  isEN: boolean,
): string | undefined => {
  const skill = findSkillByPackageName(packageName, allSkills);
  if (!skill) {
    return undefined;
  }
  const label = getSkillDisplayName(skill, isEN).trim();
  return label || undefined;
};

/** 来源列 Skill 单项 */
export type TFileSourceSkillItem = {
  /** 展示名 */
  label: string;
  /** 图标 url */
  icon: string;
};

/** 来源列解析结果 */
export type TFileSourceDisplay =
  | { kind: 'skill'; items: TFileSourceSkillItem[]; }
  | { kind: 'agent'; label: string; }
  | null;

/**
 * skills_used 命中项 → 带来源的展示项（未命中跳过）
 * @param file - 文件行
 * @param allSkills - Skill 全量列表
 * @param isEN - 是否英文界面
 */
export const resolveSkillSourceItems = (
  file: TDatabrainFile,
  allSkills: ISkillData[],
  isEN: boolean,
): TFileSourceSkillItem[] =>
  compact(
    compact(file.skills_used).map(packageName => {
      const skill = findSkillByPackageName(packageName, allSkills);
      if (!skill) {
        return undefined;
      }
      const label = getSkillDisplayName(skill, isEN).trim();
      if (!label) {
        return undefined;
      }
      return {
        label,
        icon: skill.category_icon || SkillsDefaultIcon,
      };
    }),
  );

/**
 * 解析来源列：skills_used 与 source 互斥，优先 skills_used
 * @param file - 文件行
 * @param isEN - 是否英文界面
 * @param allSkills - Skill 全量列表
 */
export const getFileSourceDisplay = (
  file: TDatabrainFile,
  isEN: boolean,
  allSkills: ISkillData[] = [],
): TFileSourceDisplay => {
  const skillsUsed = compact(file.skills_used);
  if (!isEmpty(skillsUsed)) {
    return { kind: 'skill', items: resolveSkillSourceItems(file, allSkills, isEN) };
  }
  if (file.source?.name || file.source?.name_cn) {
    const label = isEN
      ? file.source.name || file.source.name_cn || ''
      : file.source.name_cn || file.source.name || '';
    if (label) {
      return { kind: 'agent', label };
    }
  }
  return null;
};
